import type { WidgetTheme, WidgetPosition } from '../types';

/**
 * Generates scoped CSS for the widget Shadow DOM.
 * All styles are dynamically generated from the theme config.
 */
export function generateStyles(theme: WidgetTheme, position: WidgetPosition): string {
  const pos = position === 'bottom-left' ? 'left: 20px;' : 'right: 20px;';

  return /* css */ `
    :host {
      --primary: ${theme.primaryColor};
      --text: ${theme.textColor};
      --bg: ${theme.backgroundColor};
      --header-text: ${theme.headerTextColor};
      --radius: ${theme.borderRadius}px;
      --font: ${theme.fontFamily};

      all: initial;
      font-family: var(--font);
      font-size: 14px;
      line-height: 1.5;
      color: var(--text);
      z-index: 2147483647;
      position: fixed;
      bottom: 20px;
      ${pos}
    }

    *, *::before, *::after {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }

    /* === Chat Bubble (Collapsed) === */
    .chat-bubble {
      width: 60px;
      height: 60px;
      border-radius: 50%;
      background: var(--primary);
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      transition: transform 0.2s ease, box-shadow 0.2s ease;
      position: relative;
    }

    .chat-bubble:hover {
      transform: scale(1.08);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
    }

    .chat-bubble:active {
      transform: scale(0.95);
    }

    .chat-bubble:focus-visible {
      outline: 3px solid var(--primary);
      outline-offset: 3px;
      box-shadow: 0 0 0 6px rgba(99, 102, 241, 0.25);
    }

    .chat-bubble svg {
      width: 28px;
      height: 28px;
      fill: var(--header-text);
    }

    .chat-bubble .close-icon {
      width: 24px;
      height: 24px;
    }

    .badge {
      position: absolute;
      top: -4px;
      right: -4px;
      background: #ef4444;
      color: white;
      font-size: 11px;
      font-weight: 700;
      min-width: 20px;
      height: 20px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 5px;
      border: 2px solid white;
    }

    .badge.hidden { display: none; }

    .badge:not(.hidden) {
      /* Visually present but also announced via aria-live on wrapper */
    }

    /* === Chat Panel (Expanded) === */
    .chat-panel {
      width: 380px;
      max-width: calc(100vw - 40px);
      height: 640px;
      max-height: calc(100vh - 100px);
      background: var(--bg);
      border-radius: var(--radius);
      box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      position: absolute;
      bottom: 72px;
      ${position === 'bottom-left' ? 'left: 0;' : 'right: 0;'}
      opacity: 0;
      transform: translateY(10px) scale(0.95);
      transition: opacity 0.2s ease, transform 0.2s ease;
      pointer-events: none;
    }

    .chat-panel.open {
      opacity: 1;
      transform: translateY(0) scale(1);
      pointer-events: auto;
    }

    /* === Header === */
    .chat-header {
      background: var(--primary);
      color: var(--header-text);
      padding: 16px 20px;
      display: flex;
      align-items: center;
      gap: 12px;
      flex-shrink: 0;
    }

    .chat-header .header-text {
      flex: 1;
      min-width: 0;
    }

    .chat-header .header-close {
      flex-shrink: 0;
      margin-left: auto;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: transparent;
      border: none;
      border-radius: 8px;
      color: var(--header-text);
      cursor: pointer;
      transition: background 0.15s;
    }

    .chat-header .header-close:hover {
      background: rgba(255, 255, 255, 0.18);
    }

    .chat-header .header-close:focus-visible {
      outline: 2px solid var(--header-text);
      outline-offset: 1px;
    }

    .chat-header .header-close svg {
      width: 20px;
      height: 20px;
      fill: currentColor;
    }

    .chat-header .brand-logo {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      object-fit: cover;
    }

    .chat-header .header-text {
      flex: 1;
    }

    .chat-header .brand-name {
      font-size: 16px;
      font-weight: 600;
      margin: 0;
      line-height: 1.3;
    }

    .chat-header .status-text {
      font-size: 12px;
      opacity: 0.85;
    }

    /* === Messages Area === */
    .messages-area {
      flex: 1;
      overflow-y: auto;
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .messages-area::-webkit-scrollbar {
      width: 6px;
    }

    .messages-area::-webkit-scrollbar-thumb {
      background: #d1d5db;
      border-radius: 3px;
    }

    .greeting-message {
      background: #f3f4f6;
      border-radius: 12px 12px 12px 4px;
      padding: 12px 16px;
      max-width: 85%;
      font-size: 14px;
      color: var(--text);
      line-height: 1.5;
    }

    .message-bubble {
      max-width: 85%;
      padding: 10px 14px;
      border-radius: 12px;
      font-size: 14px;
      line-height: 1.5;
      word-wrap: break-word;
    }

    .message-bubble.visitor {
      align-self: flex-end;
      background: var(--primary);
      color: var(--header-text);
      border-radius: 12px 12px 4px 12px;
    }

    .message-bubble.agent,
    .message-bubble.system {
      align-self: flex-start;
      background: #f3f4f6;
      color: var(--text);
      border-radius: 12px 12px 12px 4px;
    }

    /* System/auto-reply bubbles get a subtle accent so they read as "from the system" */
    .message-bubble.system {
      border-left: 3px solid #22c55e;
    }

    .message-meta {
      font-size: 11px;
      color: #9ca3af;
      margin-top: 2px;
    }

    .message-meta.visitor-meta {
      text-align: right;
    }

    .typing-indicator {
      display: flex;
      gap: 4px;
      padding: 12px 16px;
      align-self: flex-start;
    }

    .typing-indicator span {
      width: 8px;
      height: 8px;
      background: #d1d5db;
      border-radius: 50%;
      animation: typing 1.4s infinite;
    }

    .typing-indicator span:nth-child(2) { animation-delay: 0.2s; }
    .typing-indicator span:nth-child(3) { animation-delay: 0.4s; }

    @keyframes typing {
      0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
      30% { transform: translateY(-4px); opacity: 1; }
    }

    /* === Contact Form === */
    .contact-form {
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 12px;
      /* The offline variant (intro + fields + message box + Send) can be taller than
         the panel — scroll inside the form rather than clipping the Send button. */
      flex: 1;
      min-height: 0;
      overflow-y: auto;
    }

    .contact-form::-webkit-scrollbar {
      width: 6px;
    }

    .contact-form::-webkit-scrollbar-thumb {
      background: #d1d5db;
      border-radius: 3px;
    }

    /* Never let flex compress the fields to make the form "fit" — scrolling handles overflow */
    .contact-form label,
    .contact-form input,
    .contact-form textarea,
    .contact-form .form-hint,
    .contact-form .offline-intro,
    .contact-form .submit-btn {
      flex-shrink: 0;
    }

    .contact-form label {
      font-size: 13px;
      font-weight: 500;
      color: #6b7280;
    }

    .contact-form input {
      width: 100%;
      padding: 10px 12px;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      font-size: 14px;
      font-family: var(--font);
      outline: none;
      transition: border-color 0.15s;
    }

    .contact-form input:focus {
      border-color: var(--primary);
      box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
    }

    .contact-form input:focus-visible {
      outline: 2px solid var(--primary);
      outline-offset: 1px;
    }

    .contact-form input::placeholder {
      color: #9ca3af;
    }

    .contact-form textarea {
      width: 100%;
      min-height: 72px;
      padding: 10px 12px;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      font-size: 14px;
      font-family: var(--font);
      line-height: 1.5;
      color: var(--text);
      background: var(--bg);
      resize: vertical;
      outline: none;
      transition: border-color 0.15s;
    }

    .contact-form textarea:focus {
      border-color: var(--primary);
      box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
    }

    .contact-form textarea:focus-visible {
      outline: 2px solid var(--primary);
      outline-offset: 1px;
    }

    .contact-form textarea::placeholder {
      color: #9ca3af;
    }

    .contact-form .form-hint {
      font-size: 12px;
      color: #6b7280;
      line-height: 1.4;
    }

    /* Offline (lead-capture) form — intro reads like the first chat bubble */
    .offline-form .offline-intro {
      max-width: 100%;
    }

    .contact-form .submit-btn {
      padding: 10px 16px;
      background: var(--primary);
      color: var(--header-text);
      border: none;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 600;
      font-family: var(--font);
      cursor: pointer;
      transition: opacity 0.15s;
      /* Always in view: the submit control sticks to the bottom of the scrollable
         form while the fields scroll underneath it. */
      position: sticky;
      bottom: 0;
      z-index: 1;
      box-shadow: 0 0 0 6px var(--bg), 0 -10px 14px -6px rgba(0, 0, 0, 0.18);
    }

    .contact-form .submit-btn:hover { opacity: 0.9; }
    .contact-form .submit-btn:disabled { opacity: 0.5; cursor: not-allowed; }

    .contact-form .submit-btn:focus-visible {
      outline: 2px solid var(--header-text);
      outline-offset: 2px;
    }

    /* Honeypot — visually hidden but present for bots */
    .hp-field {
      position: absolute !important;
      left: -9999px !important;
      top: -9999px !important;
      width: 1px !important;
      height: 1px !important;
      overflow: hidden !important;
      clip: rect(0, 0, 0, 0) !important;
      white-space: nowrap !important;
    }

    /* Screen-reader only utility */
    .sr-only {
      position: absolute !important;
      width: 1px !important;
      height: 1px !important;
      padding: 0 !important;
      margin: -1px !important;
      overflow: hidden !important;
      clip: rect(0, 0, 0, 0) !important;
      white-space: nowrap !important;
      border: 0 !important;
    }

    /* === Input Area === */
    .input-area {
      padding: 12px 16px;
      border-top: 1px solid #e5e7eb;
      display: flex;
      align-items: flex-end;
      gap: 8px;
      flex-shrink: 0;
    }

    .input-area textarea {
      flex: 1;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 10px 12px;
      font-size: 14px;
      font-family: var(--font);
      resize: none;
      outline: none;
      max-height: 100px;
      min-height: 40px;
      line-height: 1.4;
      transition: border-color 0.15s;
    }

    .input-area textarea:focus {
      border-color: var(--primary);
    }

    .input-area textarea:focus-visible {
      outline: 2px solid var(--primary);
      outline-offset: 1px;
    }

    .input-area textarea::placeholder {
      color: #9ca3af;
    }

    .send-btn {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: var(--primary);
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      transition: opacity 0.15s;
    }

    .send-btn:hover { opacity: 0.9; }
    .send-btn:disabled { opacity: 0.4; cursor: not-allowed; }

    .send-btn:focus-visible {
      outline: 2px solid var(--primary);
      outline-offset: 2px;
    }

    .send-btn svg {
      width: 18px;
      height: 18px;
      fill: var(--header-text);
    }

    /* === Panel Footer (powered-by + version info) === */
    .panel-footer {
      display: flex;
      align-items: center;
      padding: 4px 8px;
      border-top: 1px solid #f3f4f6;
      flex-shrink: 0;
      min-height: 24px;
    }

    .version-info {
      position: relative;
      display: flex;
      align-items: center;
      cursor: default;
    }

    .version-info svg {
      width: 14px;
      height: 14px;
      fill: #d1d5db;
      transition: fill 0.15s;
    }

    .version-info:hover svg {
      fill: #9ca3af;
    }

    .version-tooltip {
      position: absolute;
      bottom: calc(100% + 6px);
      left: 0;
      background: #1f2937;
      color: #fff;
      font-size: 11px;
      padding: 4px 8px;
      border-radius: 4px;
      white-space: nowrap;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.15s;
    }

    .version-info:hover .version-tooltip {
      opacity: 1;
    }

    /* Agent presence: green = someone is online to reply, grey = away */
    .presence-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #9ca3af;
      margin-left: 4px;
      flex-shrink: 0;
    }

    .presence-dot.online {
      background: #22c55e;
    }

    .powered-by {
      flex: 1;
      text-align: center;
      font-size: 11px;
      color: #9ca3af;
      padding: 2px 0;
    }

    .powered-by a {
      color: #6b7280;
      text-decoration: none;
    }

    .powered-by a:hover { text-decoration: underline; }

    /* === Error === */
    .error-banner {
      background: #fef2f2;
      color: #dc2626;
      padding: 8px 16px;
      font-size: 13px;
      text-align: center;
      flex-shrink: 0;
    }

    /* === Loading === */
    .loading-spinner {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
    }

    .loading-spinner::after {
      content: '';
      width: 24px;
      height: 24px;
      border: 3px solid #e5e7eb;
      border-top-color: var(--primary);
      border-radius: 50%;
      animation: spin 0.6s linear infinite;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    /* === Reduced Motion === */
    @media (prefers-reduced-motion: reduce) {
      .chat-bubble,
      .chat-panel,
      .contact-form input,
      .contact-form .submit-btn,
      .input-area textarea,
      .send-btn,
      .version-info svg,
      .version-tooltip {
        transition: none !important;
      }

      .chat-bubble:hover {
        transform: none;
      }

      .typing-indicator span {
        animation: none !important;
        opacity: 0.6;
      }

      .loading-spinner::after {
        animation: none !important;
        border-top-color: var(--primary);
        opacity: 0.6;
      }

      .chat-panel {
        transition: none !important;
      }

      .chat-panel.open {
        transform: none;
      }
    }

    /* === High Contrast Mode === */
    @media (forced-colors: active) {
      .chat-bubble {
        border: 2px solid ButtonText;
      }

      .message-bubble {
        border: 1px solid CanvasText;
      }

      .contact-form input,
      .input-area textarea {
        border: 1px solid CanvasText;
      }
    }

    /* === Mobile Responsive === */
    @media (max-width: 480px) {
      .chat-panel {
        width: 100vw;
        max-width: 100vw;
        /* dvh tracks the real visible area on mobile (iOS address bar); vh is the fallback */
        height: 100vh;
        height: 100dvh;
        max-height: 100vh;
        max-height: 100dvh;
        border-radius: 0;
        bottom: 0;
        left: 0 !important;
        right: 0 !important;
        position: fixed;
      }

      /* iOS Safari auto-zooms into inputs with font-size < 16px, wrecking the layout */
      .contact-form input,
      .contact-form textarea,
      .input-area textarea {
        font-size: 16px;
      }

      .chat-bubble {
        bottom: 0;
      }

      :host {
        bottom: 16px;
      }
    }
  `;
}
